import apiClient, { handleApiError } from '../lib/api-client';
import { API_ENDPOINTS } from '../config/api';

export interface Challenge {
  id: string;
  name: string;
  description: string;
  type: string;
  targetValue: number;
  startDate: string;
  endDate: string;
  isActive: boolean;
  userProgress?: {
    currentValue: number;
    progress: number;
  };
}

export interface LeaderboardEntry {
  userId: string;
  username?: string;
  rank: number;
  score: number;
  avatarUrl?: string;
}

export interface Achievement {
  id: string;
  name: string;
  description: string;
  iconUrl?: string;
  unlockedAt?: string;
}

export interface DailyTip {
  id: string;
  tipText: string;
  category?: string;
}

export const communityService = {
  async getChallenges(params?: { limit?: number; offset?: number }): Promise<Challenge[]> {
    try {
      const response = await apiClient.get<{ data: { data: Challenge[] } }>(
        API_ENDPOINTS.COMMUNITY.CHALLENGES,
        { params }
      );
      return response.data.data?.data || response.data.data || [];
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  },

  async joinChallenge(challengeId: string) {
    try {
      const response = await apiClient.post(API_ENDPOINTS.COMMUNITY.CHALLENGE_JOIN(challengeId));
      return response.data.data || response.data;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  },

  async getChallengeProgress(challengeId: string) {
    try {
      const response = await apiClient.get(
        API_ENDPOINTS.COMMUNITY.CHALLENGE_PROGRESS(challengeId)
      );
      return response.data.data || response.data;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  },

  async getLeaderboard(params?: {
    limit?: number;
    metricType?: string;
  }): Promise<LeaderboardEntry[]> {
    try {
      const response = await apiClient.get<{ data: { data: LeaderboardEntry[] } }>(
        API_ENDPOINTS.COMMUNITY.LEADERBOARD,
        {
          params: {
            metricType: params?.metricType ?? 'calories_burnt',
            limit: params?.limit ?? 10,
          },
        }
      );
      const raw = response.data.data?.data || response.data.data || [];
      return raw.map((e: any) => ({
        userId: e.userId,
        username: e.username,
        rank: e.rank,
        score: e.metric_value ?? e.score,
        metric_value: e.metric_value ?? e.score,
      }));
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  },

  async getAchievements(): Promise<Achievement[]> {
    try {
      const response = await apiClient.get<{ data: { data: Achievement[] } }>(
        API_ENDPOINTS.COMMUNITY.ACHIEVEMENTS
      );
      const raw = response.data.data?.data ?? response.data.data ?? response.data ?? [];
      return Array.isArray(raw) ? raw : [];
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  },

  async getDailyTip(): Promise<DailyTip | null> {
    try {
      const response = await apiClient.get<{ data?: DailyTip } | DailyTip | DailyTip[]>(
        API_ENDPOINTS.COMMUNITY.DAILY_TIPS
      );
      const body = response?.data;
      if (body == null) return null;
      const data =
        body && typeof body === 'object' && 'data' in body ? (body as { data: DailyTip }).data : body;
      // If array, return random one
      if (Array.isArray(data) && data.length > 0) {
        return data[Math.floor(Math.random() * data.length)];
      }
      return (data && typeof data === 'object' && !Array.isArray(data)) ? data as DailyTip : null;
    } catch (error) {
      // Don't throw - return null so dashboard still loads when daily tip API fails
      console.warn('Daily tip fetch failed:', error);
      return null;
    }
  },
};

